import React, { useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Card, Input, Icon, Text } from '@rneui/themed';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { SelectField } from '../../common/SelectField';
import { H1, H2 } from '../../text';
import GivingContext, { ActionTypes } from '../../../contexts/Giving';
import Styles from './styles';

const ContributionContainer = props => {
    const dispatch = props?.dispatch;
    const funds = props?.funds;
    const control = props?.control;
    const errors = props?.errors;
    const clearErrors = props?.clearErrors;
    const setValue = props?.setValue;

    const [amountSelections, setAmountSelections] = useState({
        'donations.0.amount': { start: 0, end: 0 }
    });

    const [amountIsDeleting, setAmountIsDeleting] = useState(false);

    const formatCurrency = (fieldName, amount) => {
        if (!amount) {
            return '';
        }

        amount = amount.replace('$', '').replaceAll(',', '');

        const pieces = amount.split('.');

        if (pieces[1]?.length > 2) {
            if (!amountIsDeleting) {
                const shiftingCents = pieces[1].substring(0, 1);
                pieces[0] += shiftingCents;
            }

            pieces[1] = pieces[1].substring(1, 3);
        }

        let dollars = parseInt(pieces[0]);

        if (isNaN(dollars) || dollars <= 0) {
            dollars = 0;
        }

        if (!pieces[1]) {
            pieces[1] = '00';
        } else if (pieces[1].length < 2) {
            pieces[1] = `${pieces[1]}0`;
        } else {
            pieces[1] = pieces[1].substring(0, 2);
        }

        const result = `${dollars}.${pieces[1]}`.formatCurrency();
        setValue(fieldName, result);

        return result;
    };

    const handleAppendDonation = useCallback(() => {
        dispatch({
            type: ActionTypes.ADD_DONATION
        });
    }, [dispatch]);

    const handleRemoveDonation = useCallback(
        donation => {
            if (donation) {
                dispatch({
                    type: ActionTypes.REMOVE_DONATION,
                    data: donation.id
                });
            }
        },
        [dispatch]
    );

    return (
        <>
            <View style={{ marginTop: props.state.cartDetails.donations.length > 0 ? '10pt' : undefined }}>
                {props.state.cartDetails.donations.map((donation, index) => {
                    return (
                        <View key={`donations.${donation.id}.${index}`} style={{ flexDirection: 'row' }}>
                            <Controller
                                control={control}
                                defaultValue={donation.fundCode}
                                name={`donations.${index}.fund`}
                                render={({ field }) => (
                                    <View style={{ flex: 1 }}>
                                        <View style={{ flexDirection: 'row' }}>
                                            <Text style={Styles.Form.label}>
                                                Give To
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                        </View>
                                        <SelectField
                                            {...field}
                                            containerStyle={Styles.ContributionCard.selectFieldContainer}
                                            innerRef={field.ref}
                                            onValueChange={val =>
                                                dispatch({
                                                    type: ActionTypes.UPDATE_DONATION,
                                                    data: {
                                                        id: donation.id,
                                                        amount: donation.amount,
                                                        fundCode: val
                                                    }
                                                })
                                            }
                                            options={funds}
                                            prompt='Select Gift'
                                            style={StyleSheet.flatten(Styles.ContributionCard.selectField, {
                                                backgroundColor: '#fff',
                                                fontFamily: 'GothamBook',
                                                borderColor: '#dde4e9',
                                                borderWidth: StyleSheet.hairlineWidth
                                            })}
                                            value={donation.fundCode}
                                        />
                                    </View>
                                )}
                            />
                            <Controller
                                control={control}
                                name={`donations.${index}.amount`}
                                render={({ field }) => {
                                    const fieldName = `donations.${index}.amount`;

                                    return (
                                        <View style={{ flex: 1, marginHorizontal: '4pt' }}>
                                            <Text style={Styles.Form.label}>
                                                Amount
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                            <Input
                                                containerStyle={Styles.ContributionCard.donationAmountContainer}
                                                errorMessage={errors?.donations?.[index]?.amount?.message}
                                                errorStyle={Styles.Form.errorStyle}
                                                inputContainerStyle={ContributionCard.inputContainer}
                                                inputStyle={{ borderWidth: 0, fontFamily: 'GothamMedium' }}
                                                labelStyle={{ display: 'none' }}
                                                onBlur={field.onBlur}
                                                onChange={({ nativeEvent: { target } }) => {
                                                    const formattedAmount = formatCurrency(fieldName, target.value);

                                                    const amount = formattedAmount.replace('$', '').replaceAll(',', '');
                                                    const amountInt = parseFloat(amount, 10).toFixed(2);

                                                    if (!isNaN(amountInt)) {
                                                        clearErrors(fieldName);

                                                        dispatch({
                                                            type: ActionTypes.UPDATE_DONATION,
                                                            data: {
                                                                ...donation,
                                                                amount: amountInt
                                                            }
                                                        });
                                                    } else {
                                                        dispatch({
                                                            type: ActionTypes.UPDATE_DONATION,
                                                            data: {
                                                                ...donation,
                                                                amount: ''
                                                            }
                                                        });
                                                    }

                                                    target.value = formattedAmount;

                                                    if (amountSelections.hasOwnProperty(fieldName)) {
                                                        setAmountSelections(existing => {
                                                            return {
                                                                ...existing,
                                                                [fieldName]: { start: target.selectionStart, end: target.selectionEnd }
                                                            };
                                                        });
                                                    } else {
                                                        setAmountSelections({
                                                            [fieldName]: { start: target.selectionStart, end: target.selectionEnd }
                                                        });
                                                    }
                                                }}
                                                onKeyPress={({ nativeEvent: { key: keyValue } }) => {
                                                    if (keyValue === 'Backspace') {
                                                        setAmountIsDeleting(true);
                                                    } else {
                                                        setAmountIsDeleting(false);
                                                    }
                                                }}
                                                onSelectionChange={({
                                                    nativeEvent: {
                                                        selection: { start, end },
                                                        text
                                                    }
                                                }) => {
                                                    if (field.value?.length > 0) {
                                                        if (amountSelections.hasOwnProperty(fieldName)) {
                                                            setAmountSelections(existing => {
                                                                return {
                                                                    ...existing,
                                                                    [fieldName]: { start, end }
                                                                };
                                                            });
                                                        } else {
                                                            setAmountSelections({
                                                                [fieldName]: { start, end }
                                                            });
                                                        }
                                                    }
                                                }}
                                                placeholder='$0.00'
                                                placeholderTextColor='#333333'
                                                selection={amountSelections[fieldName]}
                                                spellCheck={false}
                                                style={Styles.ContributionCard.input}
                                            />
                                        </View>
                                    );
                                }}
                                rules={{
                                    pattern: {
                                        message: 'Amount must be a numeric value.',
                                        value: new RegExp(/(0|[1-9][0-9]{0,2})(,\d{3})*(\.\d{1,2})?$/)
                                    },
                                    validate: () => {
                                        if (isNaN(donation.amount)) {
                                            return 'Amount must be a numeric value.';
                                        } else {
                                            return true;
                                        }
                                    }
                                }}
                            />
                            <Controller
                                control={control}
                                defaultValue={donation.recurrence}
                                name={`donations.${index}.recurrence`}
                                render={({ field }) => (
                                    <View style={{ flex: 1 }}>
                                        <Text style={Styles.Form.label}>Recurrence</Text>
                                        <SelectField
                                            {...field}
                                            containerStyle={Styles.ContributionCard.selectFieldContainer}
                                            innerRef={field.ref}
                                            onValueChange={val =>
                                                dispatch({
                                                    type: ActionTypes.UPDATE_DONATION,
                                                    data: {
                                                        ...donation,
                                                        recurrence: val
                                                    }
                                                })
                                            }
                                            options={props.recurrences}
                                            prompt='Select Recurrence'
                                            style={StyleSheet.flatten(Styles.ContributionCard.selectField, {
                                                borderColor: '#dde4e9',
                                                borderWidth: StyleSheet.hairlineWidth,
                                                backgroundColor: '#fff',
                                                fontFamily: 'GothamBook'
                                            })}
                                            value={donation.recurringType}
                                        />
                                    </View>
                                )}
                            />
                            {
                                /* spacer */
                                index === 0 && (
                                    <View
                                        style={{
                                            justifyContent: 'center',
                                            overflow: 'visible',
                                            minWidth: 18
                                        }}
                                    />
                                )
                            }
                            {index !== 0 && props.state.cartDetails.donations?.length > 1 && (
                                <Icon
                                    color='#FE0000'
                                    containerStyle={{
                                        flex: 1,
                                        justifyContent: 'center',
                                        overflow: 'visible',
                                        maxWidth: 18
                                    }}
                                    name='times'
                                    size={18}
                                    style={{
                                        marginLeft: 15,
                                        marginTop: 20
                                    }}
                                    type='font-awesome-5'
                                    onPress={() => handleRemoveDonation(donation)}
                                />
                            )}
                        </View>
                    );
                })}
            </View>
            <View style={Styles.ContributionCard.addDontationButtonContainer}>
                <Icon color='#FE0000' name='plus' size={12} style={{ paddingRight: 3 }} type='font-awesome-5' />
                <Text onPress={() => handleAppendDonation()} style={Styles.ContributionCard.addDontationButtonTitle}>
                    ADD AN ADDITIONAL DONATION
                </Text>
            </View>
        </>
    );
};

const ContributionCard = props => {
    const { state, dispatch } = useContext(GivingContext);
    const title = props?.title || 'Please Donate';
    const total = props?.total || 0;

    const FundOptions = useMemo(() => {
        return state.funds.map(fund => {
            return {
                label: fund.name,
                value: fund.fundCode
            };
        });
    }, [state.funds]);

    const RecurrenceOptions = useMemo(() => {
        return state.recurrences.map(recurrence => {
            return {
                label: recurrence.name,
                value: recurrence.type
            };
        });
    }, [state.recurrences]);

    return (
        <Card containerStyle={Styles.ContributionCard.container} testID='ContributionCard'>
            <View>
                <Text style={Styles.ContributionCard.requireWarning}>
                    <Text style={{ color: '#FE0000', fontWeight: 'bold' }}>* </Text> Denotes Required Field
                </Text>
            </View>
            <Card.FeaturedTitle style={Styles.ContributionCard.cardTitle}>
                <H2 text={title} />
            </Card.FeaturedTitle>
            <View style={{ alignSelf: 'center', flexDirection: 'row' }}>
                {total > 0 ? (
                    <>
                        <Text style={{ ...Styles.ContributionCard.currencySymbol, color: '#8897a2' }}>$</Text>
                        <Card.FeaturedSubtitle style={{ ...Styles.ContributionCard.subtitle, color: '#8897a2' }}>
                            <H1 text={total?.formatCurrency()?.substring(1)} />
                        </Card.FeaturedSubtitle>
                    </>
                ) : (
                    <>
                        <Text style={Styles.ContributionCard.currencySymbol}>$</Text>
                        <Card.FeaturedSubtitle style={Styles.ContributionCard.subtitle}>{total?.formatCurrency()?.substring(1)}</Card.FeaturedSubtitle>
                    </>
                )}
            </View>
            <ContributionContainer dispatch={dispatch} funds={FundOptions} recurrences={RecurrenceOptions} state={state} {...props} />
        </Card>
    );
};

ContributionCard.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    title: PropTypes.string,
    total: PropTypes.number
};

export default ContributionCard;
